import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { EmailPayloadBase } from "@models/email-templates/email-payload-base";
import { IEmailPreviewResponse } from "@models/email-templates/email-review-response.interface";
import { EmailTemplate } from "@models/email-templates/email-template";
import {
  EmailTemplateKey,
  EmailTemplatesHelper,
  IEmailAndTextTemplatePayloadData
} from "@models/email-templates/email-templates-helper";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { DocumentTypePipe } from "@shared/pipes/document-type.pipe";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { ConfigSettingsService } from "@shared/services/config-settings.service";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { RemindersApiService } from "@shared/services/reminders-api.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { ToastrService } from "ngx-toastr";
import { CurrencyPipe, formatDate } from "@angular/common";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { SendOption } from "@models/annual-statements/annual-statement-enums";
import { Contact } from "@models/common/contact";
import { TextMessageTemplate } from "@models/text-messages-templates/text-message-template";
import { catchError, combineLatest, Observable, of, tap } from "rxjs";
import { UIProfile } from "@models/organisations/ui-profile";
import { TextMessagesHelper } from "@models/text-messages-templates/text-messages-helper";
import { DocumentBase } from "@models/documents/document-base";
import { SelectOption } from "@models/ui-models/select-options";
import {
  contactWithEmailRequiredMessage,
  contactWithPhoneRequiredMessage
} from '../../../../../forms/controls/contacts-select/constants/contact-validation-messages';
import { Company } from "@models/companies/company";
import { TextMessageTemplateKey } from "@models/text-messages-templates/text-message-template-key";

interface IAdHocReminderFormControls {
  sendBy: FormControl<SendOption>;
  attachInvoice: FormControl<boolean>;
  recipientEmails: FormControl<string | null>;
  recipientPhones: FormControl<string | null>;
}

type IAdHocReminderFormGroup = FormGroup<IAdHocReminderFormControls>;

@Component({
  selector: 'app-base-ad-hoc-reminder',
  standalone: true,
  imports: [],
  template: '',
})
export class BaseAdHocReminderComponent implements OnInit {
  annualStatementsService = inject(AnnualStatementsService);
  annualStatementsApiService = inject(AnnualStatementsApiService);
  emailTemplatesService = inject(EmailsApiService);
  textMessagesTemplateService = inject(TextMessageApiService);
  organisationService = inject(OrganisationApiService);
  remindersService = inject(RemindersApiService);
  sanitizer = inject(DomSanitizer);
  configSettingsService = inject(ConfigSettingsService);
  currencyPipe = inject(CurrencyPipe);
  toastr = inject(ToastrService);
  activeModal = inject(NgbActiveModal);
  destroyRef = inject(DestroyRef);

  @Input() isAnnualReminder = true;
  @Input() documentBase: DocumentBase | null = null;
  @Input() annualPaymentDeadline: string | null = null;
  @Input() company: Company | null = null;

  readonly SendOption = SendOption;
  readonly sendByOptions: SelectOption[] = [
    { label: 'Email', value: SendOption.Email },
    { label: 'SMS', value: SendOption.Sms },
  ];
  readonly contactWithPhoneRequiredMessage = contactWithPhoneRequiredMessage;
  readonly contactWithEmailRequiredMessage = contactWithEmailRequiredMessage;

  isLoading = false;
  organisationProfile!: UIProfile;

  // Contacts
  contacts: Contact[] = [];

  isEmailContactValid = false;
  selectedRecipientEmailContact: Contact | null = null;

  isPhoneContactValid = false;
  selectedRecipientPhonesContact: Contact | null = null;

  // Email Preview Part
  emailTemplate!: EmailTemplate;
  emailTemplates: EmailTemplate[] = [];
  emailPayloadModel: Partial<IEmailAndTextTemplatePayloadData> | undefined;
  emailHtmlContent = signal<SafeHtml | null>(null);

  // SMS Preview Part
  smsTemplate!: TextMessageTemplate;
  smsTemplates: TextMessageTemplate[] = [];
  smsPayloadModel: Partial<IEmailAndTextTemplatePayloadData> | undefined;
  smsHtmlContent = signal<SafeHtml | null>(null);

  form: IAdHocReminderFormGroup = new FormGroup<IAdHocReminderFormControls>({
    sendBy: new FormControl<SendOption>(SendOption.Email, { nonNullable: true }),
    attachInvoice: new FormControl<boolean>(true, { nonNullable: true }),
    recipientEmails: new FormControl<string | null>(null, Validators.required),
    recipientPhones: new FormControl<string | null>(null, Validators.required)
  });

  ngOnInit(): void {
    this.loadModalData();
  }

  loadModalData(): void {
    this.isLoading = true;

    combineLatest(
      this.emailTemplatesService.loadEmailTemplates(),
      this.textMessagesTemplateService.loadTextMessageTemplates(),
      this.organisationService.getProfile(),
      this.getContacts()
    )
      .subscribe({
        next: ([emailTemplates, smsTemplates, organisationProfile, contacts]) => {
          this.emailTemplates = emailTemplates;
          this.smsTemplates = smsTemplates;
          this.organisationProfile = organisationProfile;
          this.contacts = contacts;
          this.isLoading = false;

          this.onModalDataLoaded();
        },
        error: (error) => {
          console.warn('[AdHocPaymentReminderComponent.loadTemplates] error: ', error);
          this.toastr.error(`Something went wrong while loading templates. Please try again later.`, 'Error');
        }
      });
  }

  onModalDataLoaded(): void {
    this.selectEmailTemplate();
    this.selectSmsTemplate();
  }

  getContacts(): Observable<Contact[]> {
    return of([]);
  }

  onSendByChanged(value: any): void {
    const sendBy = value as SendOption;

    setControlDisabled(this.form.controls.recipientPhones, sendBy !== SendOption.Sms);

    setControlDisabled(this.form.controls.attachInvoice, sendBy !== SendOption.Email);
    setControlDisabled(this.form.controls.recipientEmails, sendBy !== SendOption.Email);
  }

  onSubmitRequestError(): void {
    this.isLoading = false;

    this.form.controls.sendBy.enable();
    this.onSendByChanged(this.form.controls.sendBy.value);
  }

  // Email Templates

  selectEmailTemplate(templateCode: EmailTemplateKey = this.emailTemplateCode): void {
    const emailTemplate = this.emailTemplates.filter(template => template.code === templateCode);

    if (emailTemplate.length) {
      this.emailTemplate = emailTemplate[0];

      const type = EmailTemplatesHelper.buildEmailTemplatePayload(templateCode).$type;
      this.emailPayloadModel = this.buildTemplatePayload(type);

      this.loadEmailPreview().subscribe(() => {
        this.isLoading = false;
      });
    }
  }

  loadEmailPreview(): Observable<IEmailPreviewResponse> {
    const errorFormResponse = of({ subject: '', htmlContent: '' });

    return this.emailTemplatesService.loadPreview(EmailTemplatesHelper.buildEmailPreviewRequest(this.buildEmailTemplate(), this.emailPayloadModel as EmailPayloadBase))
      .pipe(
        tap({
          next: (res) => {
            this.emailHtmlContent.set(this.sanitizer.bypassSecurityTrustHtml(res.htmlContent) ?? '');
            this.emailTemplate.subjectTemplate = res.subject;
          },
          error: (error) => {
            console.warn('[loadPreview] error: ', error);
            this.toastr.error("Something happened while loading preview", "Error");
            this.isLoading = false;
          }
        }),
        catchError(() => errorFormResponse),
      );
  }

  buildEmailTemplate(): EmailTemplate {
    return new EmailTemplate({
      ...this.emailTemplate,
      subjectTemplate: this.emailTemplate?.subjectTemplate,
      bodyTemplate: this.emailTemplate.bodyTemplate,
      footerTemplate: this.emailTemplate.footerTemplate,
    });
  }

  // SMS Templates

  selectSmsTemplate(templateCode: TextMessageTemplateKey = this.textMessageTemplateCode): void {
    if (this.smsTemplates) {
      const smsTemplate = this.smsTemplates.filter((template) => template.code === templateCode);
      this.smsTemplate = smsTemplate[0];

      const type = TextMessagesHelper.buildTextMessageTemplatePayload(templateCode).$type;
      this.smsPayloadModel = this.buildTemplatePayload(type);

      const replacedSmsTemplate = this.smsTemplate.template
        .replace('{{Debt}}', this.smsPayloadModel.debt ?? '')
        .replace('{{DueDate}}', this.smsPayloadModel.dueDate ?? '')
        .replace('{{CompanyName}}', this.smsPayloadModel.companyName ?? '')
        .replace('{{AnnualReviewFeeDeadline}}', this.smsPayloadModel.annualReviewFeeDeadline ?? '')
        .replace('{{AnnualReviewFee}}', this.smsPayloadModel.annualReviewFee ?? '')
        .replace('{{OrganisationName}}', this.smsPayloadModel.organisationName ?? '')
        .replace('{{OrganisationPhone}}', this.smsPayloadModel.organisationPhone ?? '')
        .replace('\n', '<br>');

      this.smsHtmlContent.set(this.sanitizer.bypassSecurityTrustHtml(replacedSmsTemplate));
    }
  }

  buildTemplatePayload(type: string): Partial<IEmailAndTextTemplatePayloadData> {
    const company = this.documentBase?.company ?? this.company;
    if (!this.organisationProfile || !company) return {};

    const billingContact: Contact | null = company?.billingContact ?? company?.signingContact ?? null;
    const billingContactFullName = [billingContact?.firstName, billingContact?.lastName].filter(Boolean).join(' ').trim();

    const payload = {
      $type: type,
      organisationName: this.organisationProfile?.organisationName ?? '',
      organisationEmail: this.organisationProfile?.email ?? '',
      organisationPhone: this.organisationProfile?.phoneNumber ?? '',
      documentName: `ASIC ${ DocumentTypePipe.formatValue(this.documentBase?.type ?? '') }`,
      companyName: company?.name ?? '',
      directorFirstName: this.documentBase?.changeAuthorisation?.authorisingParty?.individualDataOverride?.firstName ?? this.documentBase?.changeAuthorisation?.chairperson?.individualDataOverride?.firstName ?? '',
      directorFullName: this.documentBase?.changeAuthorisation?.authorisingParty?.individualDataOverride?.fullNameLabel ?? this.documentBase?.changeAuthorisation?.chairperson?.individualDataOverride?.fullNameLabel ?? '',
      username: this.organisationService.getCurrentUser()?.fullName ?? '',
      companyACN: company?.acn ?? '',
      partner: company?.partnerManager?.fullName ?? '',
      asicContact: '',
      accountManager: company?.accountManager?.fullName ?? '',
      billingContactFirstName: billingContact?.firstName ?? '',
      billingContactFullName: billingContactFullName.trim() ?? '',
    };

    if (this.isAnnualReminder) {
      return {
        ...payload,
        annualReviewFeeDeadline: this.annualPaymentDeadline ? formatDate(this.annualPaymentDeadline, 'dd MMM yyyy', 'en-US') : '',
        annualReviewFeeDeadlineShortFormat: this.annualPaymentDeadline ? formatDate(this.annualPaymentDeadline, 'dd/MM/yyyy', 'en-US') : '',
        annualReviewFee: this.currencyPipe.transform(company?.companyDebt?.amountOwing ?? 0, '$') ?? '',
      };
    } else {
      return {
        ...payload,
        debt: this.currencyPipe.transform(company?.companyDebt?.amountOwing ?? 0, '$') ?? '',
        dueDate: '',
        dueDateShortFormat: '',
      };
    }
  }

  onContactCreated(contact: Contact, control: FormControl<string | null>): void {
    this.contacts.push(contact);
    control.setValue(contact.contactId);
  }

  onRecipientEmailChanged(contact: Contact | null): void {
    this.selectedRecipientEmailContact = contact;
    this.isEmailContactValid = Boolean(contact?.email);
  }

  onRecipientPhoneChanged(contact: Contact | null): void {
    this.selectedRecipientPhonesContact = contact;
    this.isPhoneContactValid = Boolean(contact?.phone);
  }

  isSelectedContactValid(): boolean {
    if (this.form.controls.sendBy.value == SendOption.Email) {
      return Boolean(this.form.controls.recipientEmails.value
        && this.selectedRecipientEmailContact
        && this.isEmailContactValid);

    } else if (this.form.controls.sendBy.value == SendOption.Sms) {
      return Boolean(this.form.controls.recipientPhones.value
        && this.selectedRecipientPhonesContact
        && this.isPhoneContactValid);
    }

    return false;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  get textMessageTemplateCode(): TextMessageTemplateKey {
    return this.isAnnualReminder
      ? 'as-fee-reminder'
      : 'company-debt-reminder';
  }

  get emailTemplateCode(): EmailTemplateKey {
    return this.isAnnualReminder
      ? 'as-debt-reminder'
      : 'asic-debt-reminder';
  }
}
